import { handleCancelEditionPanel } from '@features/divisions/containers/SingleDivisionContainer/SingleDivisionContainer.handlers';
import { FormType } from '@features/divisions/containers/SingleDivisionContainer/SingleDivisionContainer.types';
import { DivisionEditionFormType } from '@features/divisions/interfaces/division-edition.interface';
import ProcessCreationFormType from '@features/processes/interfaces/process-creation.interface';
import { ProcessEditionFormType } from '@features/processes/interfaces/process-edition.interface';
import { DivisionCliente, ProcesoDivision } from '@interfaces/firestore-documents.interfaces';
import { RequestFn } from '@interfaces/requests.interfaces';
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';
import { Dispatch, SetStateAction } from 'react';
import { UseFormReturn } from 'react-hook-form';

export function handleAddGuideRangeForm(
	state: boolean,
	setIsAddGuidesRangeFormOpen: Dispatch<SetStateAction<boolean>>
): () => void {
	return function () {
		setIsAddGuidesRangeFormOpen(state);
	};
}

export function handleSaveDivisionEditionData(
	afterSaveOnBeforeCloseDialogAction: (() => void) | null,
	defaultData: DivisionCliente,
	divisionEditionForm: UseFormReturn<DivisionEditionFormType>,
	enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
	nit: string,
	processCreationForm: UseFormReturn<ProcessCreationFormType>,
	processEditionForm: UseFormReturn<ProcessEditionFormType>,
	request: RequestFn,
	requestAdministraCliente: RequestFn,
	setAfterSaveOnBeforeCloseDialogAction: Dispatch<SetStateAction<(() => void) | null>>,
	setBeforeCloseDialog: Dispatch<SetStateAction<boolean>>,
	setBeforeSaveData: Dispatch<SetStateAction<ProcessEditionFormType | null>>,
	setBeforeSaveDialog: Dispatch<SetStateAction<string>>,
	setEditionProcessData: Dispatch<SetStateAction<ProcesoDivision | null>>,
	setForm: Dispatch<SetStateAction<FormType>>,
	setShow: React.Dispatch<React.SetStateAction<boolean>>,
	getDataById: (identificacion: string) => Promise<void>
): () => void {
	return divisionEditionForm.handleSubmit(async function (newData) {
		try {
			setShow(true);
			// TODO: Guardar edición del rango en uso.
			// TODO: Guardar edición de rangos de AGW.

			// Comprobamos si se cambiaron los combos de la división.
			const defaultCombos = defaultData.combos
				.map((c) => Number(c.id_combo))
				.sort((a, b) => a - b);

			const newCombos = newData.combos.map(Number).sort((a, b) => a - b);

			const combosToAdd = newCombos.filter((x) => !defaultCombos.includes(x));
			if (combosToAdd.length > 0) {
				const response = await request(
					'clientes/cm-clientes-ms-combos/multiples-combos',
					'PUT',
					{ identificacion: nit, codigo_div: defaultData.codigo_div, combos: combosToAdd }
				);

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			const combosToRemove = defaultCombos.filter((x) => !newCombos.includes(x));
			if (combosToRemove.length > 0) {
				const response = await request(
					'clientes/cm-clientes-ms-combos/multiples-combos',
					'DELETE',
					{
						identificacion: nit,
						codigo_div: defaultData.codigo_div,
						combos: combosToRemove,
					}
				);

				if (response.isError) {
					throw new Error(response.message);
				}
			}
			const newRanges = newData.agw_ranges.filter((i, key) => {
				if (
					defaultData.agw_ranges[key].consecutivo_desde !== i.consecutivo_desde ||
					defaultData.agw_ranges[key].consecutivo_hasta !== i.consecutivo_hasta ||
					defaultData.agw_ranges[key].en_uso !== i.en_uso ||
					defaultData.agw_ranges[key].nombre !== i.nombre ||
					defaultData.agw_ranges[key].direccion !== i.direccion ||
					defaultData.agw_ranges[key].telefono !== i.telefono ||
					defaultData.agw_ranges[key].ciudad !== i.ciudad ||
					defaultData.agw_ranges[key].nombre_ciudad !== i.nombre_ciudad ||
					defaultData.agw_ranges[key].correo !== i.correo
				) {
					return i;
				}
			});
			if (newRanges.length > 0) {
				for (const newRange of newRanges) {
					const response = await request(
						'clientes/suite-maestro-rangos/rango-maestro',
						'PUT',
						{
							id_rango: newRange.id_rango,
							rango: {
								consecutivo_desde: newRange.consecutivo_desde,
								consecutivo_hasta: newRange.consecutivo_hasta,
								en_uso: newRange.en_uso,
							},
							detalle_rango: {
								nombre: newRange.nombre,
								direccion: newRange.direccion,
								telefono: newRange.telefono,
								ciudad: newRange.ciudad,
								nombre_ciudad: newRange.nombre_ciudad,
								correo: newRange.correo,
							},
						}
					);
					if (response.isError) {
						throw new Error(response.message);
					}
				}
			}

			// Comprobamos si se cambió el estado de la notificación a remitente.
			if (defaultData.notificacion_remitente !== newData.notificacion_remitente) {
				const response = await requestAdministraCliente(
					'divisiones/flag-notificacion',
					'PUT',
					{
						identificacion: nit,
						codigo_div: defaultData.codigo_div,
						envio_notificacion: newData.notificacion_remitente,
					}
				);

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			if (defaultData.habilitado_retencion_guia !== newData.habilitado_retencion_guia) {
				const response = await requestAdministraCliente(
					`divisiones-retenciones?id_cliente=${nit}&divisiones=${defaultData.codigo_div}`,
					'PUT',
					{
						accion: newData.habilitado_retencion_guia ? 'ACTIVAR' : 'DESACTIVAR',
					}
				);

				if (response.isError) {
					throw new Error(response.message);
				}
			}

			handleCancelEditionPanel(
				afterSaveOnBeforeCloseDialogAction,
				defaultData,
				divisionEditionForm,
				processCreationForm,
				processEditionForm,
				setAfterSaveOnBeforeCloseDialogAction,
				setBeforeCloseDialog,
				setBeforeSaveData,
				setBeforeSaveDialog,
				setEditionProcessData,
				setForm
			)();
			await getDataById(nit);
			setShow(false);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setShow(false);
			if (typeof error === 'string') {
				enqueueSnackbar(error, { variant: 'error' });
			} else if (error.response && error.response.data.message) {
				enqueueSnackbar(error.response.data.message, { variant: 'error' });
			}
		}
	});
}
